<template>
  <div class="list-container">
    <!-- Organization Selector Tabs -->
    <div v-if="organizations.length > 1" class="tabs">
      <button 
        v-for="organization in organizations" 
        :key="organization.id"
        :class="{ 'active': organization.id === selectedOrganization?.id }" 
        @click="selectOrganization(organization.id)"
      >
        {{ organization.name }}
      </button>
    </div>

    <div v-if="selectedOrganization" class="details-container">
      <!-- Organization Header -->
      <div class="details-header">
        <div class="title-stack">
          <ImageThumbnail
            v-if="selectedOrganization.logo_url"
            :url="selectedOrganization.logo_url"
            max-height="100px"
          />
          <h1>{{ selectedOrganization.name }}</h1>
          <RoleBadges :roles="selectedOrganization.member_roles" />
        </div>
        <div class="spacer"></div>
        <div class="details-actions" v-if="canEditOrganization(selectedOrganization.id)">
          <router-link
            :to="`/organizations/${selectedOrganization.id}/edit`"
            class="important-button big"
          >
            <font-awesome-icon icon="edit" /> Edit Organization
          </router-link>
        </div>
      </div>

      <!-- Organization Details -->
      <div class="details-section">
        <h3>Information</h3>
        <div class="details-group half-width">
          <label>Website URL</label>
          <div class="details-value">
            <a v-if="selectedOrganization.website_url" :href="selectedOrganization.website_url" target="_blank" rel="noopener noreferrer">
              {{ selectedOrganization.website_url }}
            </a>
          </div>
        </div>

        <div class="details-group half-width">
          <label>Phone</label>
          <div class="details-value">{{ selectedOrganization.phone }}</div>
        </div>

        <div class="details-group full-width">
          <label>Address</label>
          <div class="details-value">
            <div v-if="address1">{{ address1 }}</div>
            <div v-if="address2">{{ address2 }}</div>
            <div v-if="address3">{{ address3 }}</div>
            <div v-if="address4">{{ address4 }}</div>
          </div>
        </div>
      </div>

      <!-- Organization Members -->
      <OrganizationMembers :organization-id="selectedOrganization.id" />
    </div>

    <div v-else>
      <div class="details-header">
        <h1>Organization</h1>
      </div>
      <div class="message no-data">
        You are not a member of an organization.
      </div>
      <div class="message no-data">
        <router-link
          :to="`/organizations/new`"
          class="important-button big"
        >
          <font-awesome-icon icon="plus" /> New Organization
        </router-link>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import axios from 'axios';
import ImageThumbnail from '@/components/ImageThumbnail.vue';
import OrganizationMembers from '@/components/OrganizationMembers.vue';
import RoleBadges from '@/components/RoleBadges.vue';
import { FontAwesomeIcon } from '@/scripts/icons';
import { handleApiError } from '@/scripts/shared-utils';
import { canEditOrganization } from '@/scripts/permissions';

const organizations = ref([]);
const selectedOrganization = ref(null);

const address1 = computed(() => {
  return (selectedOrganization.value.address_line_1 || '').trim();
});

const address2 = computed(() => {
  return (selectedOrganization.value.address_line_2 || '').trim();
});

const address3 = computed(() => {
  const line = `${selectedOrganization.value.city || ''}${selectedOrganization.value.city && selectedOrganization.value.state ? ', ' : ''} ${selectedOrganization.value.state || ''}`;
  return line.trim() || '';
});

const address4 = computed(() => {
  const line = `${selectedOrganization.value.postal_code || ''} ${selectedOrganization.value.country || ''}`;
  return line.trim() || '';
});

const fetchOrganizations = async () => {
  try {
    const { data } = await axios.get('/api/users/me/organizations');
    organizations.value = data;
    if (organizations.value.length > 0) {
      selectedOrganization.value = organizations.value[0];
    } else {
      selectedOrganization.value = null;
    }
  } catch (error) {
    handleApiError(error, 'fetching organizations');
  }
};

const selectOrganization = (id) => {
  selectedOrganization.value = organizations.value.find(org => org.id === id) || null;
};

onMounted(() => {
  fetchOrganizations();
});
</script>

<style scoped>
@import '@/styles/shared-tabs.css';
@import '@/styles/shared-details.css';
</style>
